import { useState, useEffect, type FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AgentTokenSection, AGENT_TOKEN_ENV } from '@/components/nodes/AgentTokenSection';
import { NodeCreatedView } from '@/components/nodes/NodeCreatedView';
import {
  PleskSettingsSection,
  type PleskCredentials,
} from '@/components/nodes/PleskSettingsSection';
import { validateNodeForm, type NodeFormErrors } from '@/components/nodes/nodeForm';
import { DEFAULT_NODE_TYPE, NODE_TYPE_LIST, getNodeType } from '@/config/nodeTypes';
import { getApiErrorMessage } from '@/api/errors';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useConfirm } from '@/hooks/useConfirm';
import type {
  Node,
  NodeMode,
  NodeType,
  CreateNodeRequest,
  CreateNodeResponse,
  UpdateNodeRequest,
  TestPleskRequest,
  TestPleskResponse,
} from '@/types/node';
import './NodeModal.css';

/**
 * NodeModal component props
 */
export interface NodeModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when the modal should close
   */
  onClose: () => void;

  /**
   * Node to edit (if editing mode)
   */
  node?: Node | null;

  /**
   * Create a node; resolves with the one-time agent token
   */
  onCreate: (data: CreateNodeRequest) => Promise<CreateNodeResponse>;

  /**
   * Update a node
   */
  onUpdate: (id: string, data: UpdateNodeRequest) => Promise<Node>;

  /**
   * Test Plesk credentials
   */
  onTestPlesk: (data: TestPleskRequest) => Promise<TestPleskResponse>;

  /**
   * Rotate the agent API token; resolves with the new token.
   * Omit for users who may not rotate tokens (the action is then hidden).
   */
  onRotateAgentToken?: (nodeId: string) => Promise<string>;
}

const MODE_OPTIONS: ReadonlyArray<{ value: NodeMode; label: string; description: string }> = [
  { value: 'managed', label: 'Managed', description: 'Controlled by Control Plane API' },
  { value: 'standalone', label: 'Standalone', description: 'Independent operation with local SQLite' },
];

const EMPTY_PLESK: PleskCredentials = { host: '', login: '', password: '' };

/**
 * NodeModal component for creating and editing CDN nodes.
 *
 * Type-specific sections are driven by the node type registry
 * (config/nodeTypes.ts). The agent API token can be set on create/update and
 * rotated by admins; a new token is shown exactly once.
 *
 * @example
 * ```tsx
 * <NodeModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   node={selectedNode}
 *   onCreate={createNode}
 *   onUpdate={updateNode}
 *   onTestPlesk={testPlesk}
 *   onRotateAgentToken={can('nodes:rotate-token') ? rotateAgentToken : undefined}
 * />
 * ```
 */
export function NodeModal({
  isOpen,
  onClose,
  node,
  onCreate,
  onUpdate,
  onTestPlesk,
  onRotateAgentToken,
}: NodeModalProps) {
  // Form state
  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [ipv6Address, setIpv6Address] = useState('');
  const [agentPort, setAgentPort] = useState('9090');
  const [mode, setMode] = useState<NodeMode>('managed');
  const [nodeType, setNodeType] = useState<NodeType>(DEFAULT_NODE_TYPE);
  const [plesk, setPlesk] = useState<PleskCredentials>(EMPTY_PLESK);
  const [dnsEnabled, setDnsEnabled] = useState(false);
  const [routeViaVPN, setRouteViaVPN] = useState(false);
  const [agentToken, setAgentToken] = useState('');
  const [errors, setErrors] = useState<NodeFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  /** Token returned on create - shown once on the success screen */
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  /** Token returned by a rotation - shown once until the modal closes */
  const [rotatedToken, setRotatedToken] = useState<string | null>(null);

  const { confirm, confirmDialog } = useConfirm();
  const rotate = useAsyncAction(
    async (nodeId: string): Promise<string> => {
      if (!onRotateAgentToken) {
        throw new Error('Token rotation is not available');
      }
      return onRotateAgentToken(nodeId);
    },
    { errorMessage: 'Failed to rotate agent token' }
  );

  const isEditMode = !!node;
  const typeDefinition = getNodeType(nodeType);
  const { capabilities } = typeDefinition;

  // Reset form when modal opens or node changes
  useEffect(() => {
    if (!isOpen) return;

    setName(node?.name ?? '');
    setHostname(node?.hostname ?? '');
    setIpAddress(node?.ip_address ?? '');
    setIpv6Address(node?.ipv6_address ?? '');
    setAgentPort(String(node?.agent_port ?? 9090));
    setMode(node?.mode ?? 'managed');
    setNodeType(getNodeType(node?.type).type);
    // Never show the existing password
    setPlesk({ host: node?.plesk_host ?? '', login: node?.plesk_login ?? '', password: '' });
    setDnsEnabled(node?.dns_enabled ?? false);
    setRouteViaVPN(node?.route_via_vpn ?? false);
    setAgentToken('');
    setErrors({});
    setSubmitError(null);
    setCreatedToken(null);
    setRotatedToken(null);
  }, [isOpen, node]);

  /**
   * Validate the form (rules live in nodes/nodeForm.ts)
   */
  const validateForm = (): boolean => {
    const newErrors = validateNodeForm(
      { name, hostname, ipAddress, ipv6Address, agentPort, agentToken, plesk },
      { isEditMode, capabilities }
    );
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Fields shared by create and update
   */
  const buildCommonFields = () => {
    const token = agentToken.trim();
    return {
      name: name.trim(),
      hostname: hostname.trim(),
      ip_address: ipAddress.trim(),
      agent_port: parseInt(agentPort, 10),
      mode,
      route_via_vpn: capabilities.supportsVpnRouting ? routeViaVPN : false,
      ...(token ? { agent_token: token } : {}),
      ...(capabilities.hasPleskCredentials
        ? {
            plesk_host: plesk.host.trim(),
            ...(plesk.login.trim() ? { plesk_login: plesk.login.trim() } : {}),
            ...(plesk.password.trim() ? { plesk_password: plesk.password.trim() } : {}),
            dns_enabled: capabilities.supportsDns ? dnsEnabled : false,
          }
        : {}),
    };
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    const ipv6 = ipv6Address.trim();
    try {
      if (node) {
        // An empty IPv6 address clears the stored one
        await onUpdate(node.id, { ...buildCommonFields(), ipv6_address: ipv6 });
        onClose();
      } else {
        const created = await onCreate({
          ...buildCommonFields(),
          ...(ipv6 ? { ipv6_address: ipv6 } : {}),
          type: nodeType,
        });
        // Show the one-time agent token
        setCreatedToken(created.token);
      }
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, 'Failed to save node'));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Rotate the agent token after confirmation; the new token is shown once
   */
  const handleRotate = async () => {
    if (!node) return;
    const confirmed = await confirm({
      title: 'Rotate agent token',
      message: (
        <>
          Generate a new agent API token for <strong>{node.name}</strong>? The current token stops
          working immediately: the control plane cannot reach the agent until{' '}
          <code>{AGENT_TOKEN_ENV}</code> is updated on the node.
        </>
      ),
      confirmLabel: 'Rotate',
      variant: 'primary',
    });
    if (!confirmed) return;

    setRotatedToken(null);
    const result = await rotate.run(node.id);
    if (result.ok) setRotatedToken(result.value);
  };

  // If a node was created, show its token once
  if (createdToken !== null) {
    return <NodeCreatedView isOpen={isOpen} onClose={onClose} nodeName={name} token={createdToken} />;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Node' : 'Add Node'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" form="node-form" isLoading={isSaving}>
            {isEditMode ? 'Update Node' : 'Create Node'}
          </Button>
        </>
      }
    >
      <form id="node-form" onSubmit={handleSubmit} className="node-modal-form" noValidate>
        {submitError && (
          <div className="node-modal-error" role="alert">
            {submitError}
          </div>
        )}

        <Input
          label="Name"
          placeholder="Enter node name (e.g., edge-us-west-1)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          required
          disabled={isSaving}
        />

        <Input
          label="Hostname"
          placeholder="Enter hostname (e.g., cdn1.example.com)"
          value={hostname}
          onChange={(e) => setHostname(e.target.value)}
          error={errors.hostname}
          required
          disabled={isSaving}
        />

        <Input
          label="IP Address"
          placeholder="Enter IP address (e.g., 192.168.1.100)"
          value={ipAddress}
          onChange={(e) => setIpAddress(e.target.value)}
          error={errors.ip_address}
          helperText="Primary address: the agent API is reached here (IPv4 or IPv6)"
          required
          disabled={isSaving}
        />

        <Input
          label="IPv6 Address"
          placeholder="Optional (e.g., 2a0e:4001:20:d::)"
          value={ipv6Address}
          onChange={(e) => setIpv6Address(e.target.value)}
          error={errors.ipv6_address}
          helperText="Optional public IPv6 address: DNS AAAA records of the node's domains point here"
          disabled={isSaving}
        />

        <Input
          label="Agent Port"
          type="number"
          min={1}
          max={65535}
          placeholder="9090"
          value={agentPort}
          onChange={(e) => setAgentPort(e.target.value)}
          error={errors.agent_port}
          helperText="Port where the node agent listens"
          disabled={isSaving}
        />

        <div className="node-modal-field" role="radiogroup" aria-label="Node Type">
          <span className="node-modal-label">
            Node Type
            <span className="node-modal-required">*</span>
          </span>
          <div className="node-modal-mode-options">
            {NODE_TYPE_LIST.map((definition) => (
              <label key={definition.type} className="node-modal-radio">
                <input
                  type="radio"
                  name="type"
                  value={definition.type}
                  checked={nodeType === definition.type}
                  onChange={() => setNodeType(definition.type)}
                  disabled={isSaving || isEditMode}
                />
                <span className="node-modal-radio-label">
                  <strong>{definition.formLabel}</strong>
                  <span className="node-modal-radio-description">{definition.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {capabilities.hasPleskCredentials && (
          <PleskSettingsSection
            values={plesk}
            onChange={(field, value) => setPlesk((prev) => ({ ...prev, [field]: value }))}
            errors={errors.plesk ?? {}}
            supportsDns={capabilities.supportsDns}
            dnsEnabled={dnsEnabled}
            onDnsEnabledChange={setDnsEnabled}
            isEditMode={isEditMode}
            disabled={isSaving}
            onTestConnection={onTestPlesk}
          />
        )}

        <div className="node-modal-field" role="radiogroup" aria-label="Mode">
          <span className="node-modal-label">
            Mode
            <span className="node-modal-required">*</span>
          </span>
          <div className="node-modal-mode-options">
            {MODE_OPTIONS.map((option) => (
              <label key={option.value} className="node-modal-radio">
                <input
                  type="radio"
                  name="mode"
                  value={option.value}
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                  disabled={isSaving}
                />
                <span className="node-modal-radio-label">
                  <strong>{option.label}</strong>
                  <span className="node-modal-radio-description">{option.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {capabilities.supportsVpnRouting && (
          <label className="node-modal-checkbox">
            <input
              type="checkbox"
              checked={routeViaVPN}
              onChange={(e) => setRouteViaVPN(e.target.checked)}
              disabled={isSaving}
            />
            <span className="node-modal-checkbox-label">
              <strong>Route via VPN</strong>
              <span className="node-modal-checkbox-description">
                Proxy traffic through local VPN endpoint (127.0.0.1:8443)
              </span>
            </span>
          </label>
        )}

        <AgentTokenSection
          isEditMode={isEditMode}
          hasAgentToken={(node?.has_agent_token ?? false) || rotatedToken !== null}
          value={agentToken}
          onChange={setAgentToken}
          error={errors.agent_token ?? rotate.error ?? undefined}
          disabled={isSaving}
          onRotate={onRotateAgentToken ? handleRotate : undefined}
          isRotating={rotate.isPending()}
          rotatedToken={rotatedToken}
        />
      </form>
      {confirmDialog}
    </Modal>
  );
}
